import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, addDoc, serverTimestamp, deleteDoc } from '../../lib/auditedFirestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import ExamAttendanceSettings from '../attendance/ExamAttendanceSettings';
import Spinner from '../../components/Spinner';
import Skeleton from '../../components/Skeleton'; 
import DownloadReportButton from './components/DownloadReportButton';

const WaitingRoomListModal = ({ exam, onClose }) => {
    const [waitingStudents, setWaitingStudents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Set up a real-time listener for the waiting room subcollection
    useEffect(() => {
        const waitingRoomQuery = query(collection(db, 'exams', exam.id, 'waitingRoom'));

        const unsubscribe = onSnapshot(waitingRoomQuery, (querySnapshot) => {
            const students = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setWaitingStudents(students);
            setIsLoading(false);
        }, (error) => {
            console.error("Error listening to waiting room:", error);
            setIsLoading(false);
        });

        // Clean up the listener when the modal is closed
        return () => unsubscribe();
    }, [exam.id]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-2xl max-w-lg w-full max-h-[70vh] flex flex-col">
                <h3 className="text-xl font-bold mb-4">Waiting Room for {exam.title}</h3>
                
                <div className="flex-grow overflow-y-auto">
                    {isLoading ? (
                        <div className="flex justify-center py-4">
                            <Spinner color="border-purple-500" />
                        </div>
                    ) : waitingStudents.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">No students are currently waiting.</p>
                    ) : (
                        <div className="space-y-2">
                            {waitingStudents.map(student => (
                                <div key={student.id} className="p-3 border rounded-lg bg-gray-50">
                                    <p className="font-semibold text-gray-800">{student.name || 'Student'}</p>
                                    <p className="text-sm text-gray-600">{student.email}</p>
                                    <p className="text-xs text-gray-400">Joined: {student.joinedAt?.toDate().toLocaleString() || 'Joining...'}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                
                <div className="flex justify-end mt-6">
                    <button
                        onClick={onClose}
                        className="bg-gray-300 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-400"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

const ExamListAdmin = ({ paper, onEdit, onDelete, onCreateNew, onBack, setModal, onViewSubmissions, onViewAnalytics }) => { 
    const [exams, setExams] = useState([]);
    const [attendanceExam,setAttendanceExam]=useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [viewingWaitingRoomFor, setViewingWaitingRoomFor] = useState(null);
    
    // --- NEW: Search State ---
    const [searchTerm, setSearchTerm] = useState('');

    // State for student counts
    const [totalStudentsInPaper, setTotalStudentsInPaper] = useState(0);
    const [waitingRoomCounts, setWaitingRoomCounts] = useState({}); // { examId: count }

    useEffect(() => {
        if (!paper || !paper.id) return;
        const q = query(collection(db, "exams"), where("paperId", "==", paper.id));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const examsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setExams(examsData);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [paper]);

    // Effect to get total students in the paper
    useEffect(() => {
        if (!paper || !paper.id) return;

        const q = query(collection(db, "users"), where("role", "==", "student"), where("assignedPapers", "array-contains", paper.id));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            setTotalStudentsInPaper(querySnapshot.size);
        }, (error) => {
            console.error("Error fetching total student count:", error);
            setTotalStudentsInPaper(0);
        });

        return () => unsubscribe();
    }, [paper.id]);

    // Effect to listen to all waiting rooms for invigilated exams
    useEffect(() => {
        const unsubscribers = [];
        setWaitingRoomCounts({}); // Clear old counts on exam list change

        exams.forEach(exam => {
            if (exam.mode === 'invigilated') {
                const q = query(collection(db, 'exams', exam.id, 'waitingRoom'));
                const unsub = onSnapshot(q, (snapshot) => {
                    setWaitingRoomCounts(prevCounts => ({
                        ...prevCounts,
                        [exam.id]: snapshot.size
                    }));
                }, (error) => {
                    console.error(`Error listening to waiting room for exam ${exam.id}:`, error);
                });
                unsubscribers.push(unsub);
            }
        });

        // Cleanup function
        return () => {
            unsubscribers.forEach(unsub => unsub());
        };
    }, [exams]); // Re-run when the list of exams changes

    // Handlers for invigilation
    const updateInvigilationStatus = async (examId, status) => {
        try {
            const examRef = doc(db, 'exams', examId);
            await updateDoc(examRef, { invigilationStatus: status });
            setModal({ show: true, message: `Exam status updated to ${status}.` });
        } catch (error) {
            console.error("Error updating exam status:", error);
            setModal({ show: true, message: `Error: ${error.message}` });
        }
    };

    // --- Toggle Visibility Handler with Notification ---
    const toggleExamVisibility = async (exam) => {
        try {
            const newHiddenStatus = !exam.isHidden;
            const examRef = doc(db, 'exams', exam.id);
            await updateDoc(examRef, { isHidden: newHiddenStatus });
            
            // Trigger Notification if Unhiding (making visible)
            if (!newHiddenStatus) {
                await addDoc(collection(db, "notifications"), {
                    type: 'exam',
                    title: `Exam Available: ${paper.name}`,
                    message: `The exam "${exam.title}" is now open.`,
                    paperId: paper.id,
                    targetAudience: ['student'],
                    createdAt: serverTimestamp()
                });
                setModal({ show: true, message: `Exam is now visible and students notified.` });
            } else {
                setModal({ show: true, message: `Exam is now hidden.` });
            }

        } catch (error) {
            console.error("Error toggling visibility:", error);
            setModal({ show: true, message: `Error updating visibility: ${error.message}` });
        }
    };

    // --- NEW: Enhanced Deletion Logic (Cleans up Firebase Storage) ---
    const handleEnhancedDelete = async (exam) => {
        if (!window.confirm(`Are you sure you want to delete "${exam.title}"? This deletes the exam and all its versions.`)) return;

        setIsLoading(true);
        try {
            const deletePromises = [];

            // 1. Delete the working draft
            if (exam.questionsUrl) {
                deletePromises.push(deleteObject(ref(storage, `exam-questions/${exam.id}.json`)).catch(e => console.log("Draft not found")));
            }

            // 2. Delete all manual versions
            if (exam.versions && exam.versions.length > 0) {
                exam.versions.forEach(v => {
                    const versionRef = ref(storage, `exam-questions/${exam.id}_v${v.id}.json`);
                    deletePromises.push(deleteObject(versionRef).catch(e => console.log("Version not found")));
                });
            }

            // 3. Delete all auto-saves
            if (exam.autoSaves && exam.autoSaves.length > 0) {
                exam.autoSaves.forEach(save => {
                    const autoSaveRef = ref(storage, `exam-questions/${exam.id}_auto_${save.id}.json`);
                    deletePromises.push(deleteObject(autoSaveRef).catch(e => console.log("Autosave not found")));
                });
            }

            // Wait for all storage deletions to finish
            await Promise.all(deletePromises);

            // Finally, delete the Firestore document
            await deleteDoc(doc(db, 'exams', exam.id));
            
            // Trigger parent onDelete if it handles other UI state cleanup
            if (onDelete) onDelete(exam.id);

            setModal({ show: true, message: `Exam completely deleted.` });
        } catch (error) {
            console.error("Error deleting exam files:", error);
            setModal({ show: true, message: `Error deleting exam: ${error.message}` });
        } finally {
            setIsLoading(false);
        }
    };

    // --- Filter Logic ---
    const filteredExams = exams.filter(exam => 
        exam.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            {attendanceExam && <ExamAttendanceSettings exam={attendanceExam} onClose={()=>setAttendanceExam(null)}/>}
            {viewingWaitingRoomFor && (
                <WaitingRoomListModal
                    exam={viewingWaitingRoomFor}
                    onClose={() => setViewingWaitingRoomFor(null)}
                />
            )}

            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <div>
                    <button onClick={onBack} className="text-sm text-purple-600 hover:underline mb-2">&larr; Back to Papers</button>
                    <h2 className="text-2xl font-bold text-gray-800">Manage Exams for {paper.name}</h2>
                </div>
                
                {/* --- Search Bar & Create Button Wrapper --- */}
                <div className="flex gap-3 w-full md:w-auto">
                    <input 
                        type="text" 
                        placeholder="Search exams..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="p-2 border rounded-lg flex-grow md:w-64"
                    />
                    <button onClick={onCreateNew} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-2 px-4 rounded-lg hover:from-purple-700 hover:to-pink-700 whitespace-nowrap">
                        Create New Exam
                    </button>
                </div>
            </div>

            <div className="space-y-4">
                {isLoading ? (
                    <Skeleton type="row" count={4} />
                ) : filteredExams.length > 0 ? filteredExams.map(exam => {
                    // Get mode and status
                    const examMode = exam.mode || 'on-demand';
                    const invigilationStatus = exam.invigilationStatus || 'pending';
                    const isHidden = exam.isHidden === true; 

                    // Get waiting count
                    const waitingCount = waitingRoomCounts[exam.id] || 0;

                    return (
                        <div 
                            key={exam.id} 
                            className={`p-4 border rounded-lg flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 transform ${isHidden ? 'bg-gray-100 border-gray-300 opacity-75' : 'bg-white hover:border-purple-300'}`}
                        >
                            <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <h3 className="font-bold text-xl text-gray-800">
                                        {exam.title}
                                    </h3>
                                    {/* --- NEW: Draft vs Published Status Indicator --- */}
                                    {exam.activeVersionId ? (
                                        <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-bold border border-green-200">
                                            Published (v{exam.versions?.find(v => v.id === exam.activeVersionId)?.versionNumber})
                                        </span>
                                    ) : (
                                        <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-bold border border-gray-300">
                                            Draft Only
                                        </span>
                                    )}
                                    {isHidden && <span className="text-xs font-bold bg-gray-600 text-white px-2 py-0.5 rounded shadow-sm">HIDDEN</span>}
                                </div>
                                
                                {/* --- FIX: Use questionCount if available, fallback to length, then 0 --- */}
                                <p className="text-sm text-gray-600">
                                    {exam.questionCount || exam.questions?.length || 0} questions, {exam.duration} minutes
                                </p>
                                
                                {/* Display mode and status */}
                                <div className="flex items-center gap-2 mt-2">
                                    <span className={`text-xs font-semibold py-1 px-2 rounded-full ${examMode === 'invigilated' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                                        {examMode === 'invigilated' ? 'Invigilated' : 'On-Demand'}
                                    </span>
                                    {examMode === 'invigilated' && (
                                        <span className={`text-xs font-semibold py-1 px-2 rounded-full capitalize ${
                                            invigilationStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                            invigilationStatus === 'active' ? 'bg-purple-100 text-purple-800' :
                                            'bg-gray-100 text-gray-800'
                                        }`}>
                                            {invigilationStatus}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mt-4 xl:mt-0">
                                {/* Toggle Visibility Button */}
                                <button 
                                    onClick={() => toggleExamVisibility(exam)}
                                    className={`py-1.5 px-3 rounded-lg text-sm h-fit font-bold flex items-center gap-1 ${isHidden ? 'bg-gray-600 text-white hover:bg-gray-700' : 'bg-teal-500 text-white hover:bg-teal-600'}`}
                                    title={isHidden ? "Unhide Exam from Students" : "Hide Exam from Students"}
                                >
                                    {isHidden ? (
                                        <>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                            Unhide
                                        </>
                                    ) : (
                                        <>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                            Hide
                                        </>
                                    )}
                                </button>

                                {/* Conditional Invigilation Buttons */}
                                {examMode === 'invigilated' && invigilationStatus === 'pending' && (
                                    <div className="flex items-center gap-2">
                                        <div className="flex flex-col items-center">
                                            <button onClick={() => setViewingWaitingRoomFor(exam)} className="bg-blue-500 text-white py-1.5 px-3 rounded-lg hover:bg-blue-600 text-sm whitespace-nowrap shadow-sm">
                                                View Waiting Room
                                            </button>
                                            <span className="text-[10px] font-bold text-gray-500 mt-0.5">
                                                {waitingCount}/{totalStudentsInPaper} waiting
                                            </span>
                                        </div>
                                        <button onClick={() => updateInvigilationStatus(exam.id, 'active')} className="bg-purple-600 text-white py-1.5 px-3 rounded-lg hover:bg-purple-700 text-sm h-fit shadow-sm">
                                            Start Exam
                                        </button>
                                    </div>
                                )}
                                {examMode === 'invigilated' && invigilationStatus === 'active' && (
                                    <button onClick={() => updateInvigilationStatus(exam.id, 'completed')} className="bg-red-600 text-white py-1.5 px-3 rounded-lg hover:bg-red-700 text-sm h-fit shadow-sm">
                                        End Exam
                                    </button>
                                )}
                                {examMode === 'invigilated' && invigilationStatus === 'completed' && (
                                    <button onClick={() => updateInvigilationStatus(exam.id, 'pending')} className="bg-gray-500 text-white py-1.5 px-3 rounded-lg hover:bg-gray-600 text-sm h-fit shadow-sm">
                                        Reset Exam
                                    </button>
                                )}
                                
                                <button onClick={()=>setAttendanceExam(exam)} className="bg-pink-50 text-pink-800 border border-pink-200 py-1.5 px-3 rounded-lg text-sm h-fit">Deadline & attendance</button>
                                <button onClick={() => onViewSubmissions(exam)} className="bg-blue-500 text-white py-1.5 px-3 rounded-lg hover:bg-blue-600 text-sm h-fit shadow-sm">
                                    Submissions
                                </button>
                                
                                <button onClick={() => onViewAnalytics(exam)} className="bg-indigo-500 text-white py-1.5 px-3 rounded-lg hover:bg-indigo-600 text-sm h-fit flex items-center gap-1 shadow-sm">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>
                                    Analytics
                                </button>

                                <DownloadReportButton
                                    examId={exam.id}
                                    examTitle={exam.title}
                                    setModal={setModal}
                                />
                                <button onClick={() => onEdit(exam)} className="bg-yellow-500 text-white py-1.5 px-4 rounded-lg hover:bg-yellow-600 text-sm font-semibold h-fit shadow-sm">
                                    Edit
                                </button>
                                
                                {/* Updated Delete Button triggers handleEnhancedDelete */}
                                <button onClick={() => handleEnhancedDelete(exam)} className="bg-red-500 text-white py-1.5 px-3 rounded-lg hover:bg-red-600 text-sm font-semibold h-fit shadow-sm">
                                    Delete
                                </button>
                            </div>
                        </div>
                    )
                }) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                        <p className="text-gray-500 text-lg">No exams found matching your criteria.</p>
                    </div>
                )}
            </div>
        </div>
    );
};
export default ExamListAdmin;